<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import LineChart from 'shared/components/charts/LineChart.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isLoading = ref(false);
const chartData = ref({ labels: [], datasets: [] });

const fetchData = async () => {
  if (!props.filters.from || !props.filters.to) return;
  isLoading.value = true;
  try {
    const response = await ReportsAPI.getReports({
      metric: 'conversations_count',
      from: props.filters.from,
      to: props.filters.to,
      groupBy: 'day',
    });
    const data = response.data || [];
    chartData.value = {
      labels: data.map(d => {
        const date = new Date(d.timestamp * 1000);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      }),
      datasets: [
        {
          label: t('CEO_DASHBOARD.TICKET_VOLUME.DAILY'),
          data: data.map(d => d.value),
          borderColor: '#12A594',
          backgroundColor: 'rgba(18, 165, 148, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: '#12A594',
        },
      ],
    };
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchData, 120000);

watch(
  () => props.filters,
  () => {
    fetchData();
    stopRefetching();
    startRefetching();
  },
  { deep: true }
);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11">
      {{ t('CEO_DASHBOARD.TICKET_VOLUME.DAILY') }}
    </h3>
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>
    <div v-else class="h-64">
      <LineChart :collection="chartData" />
    </div>
  </div>
</template>
